<template>
  <aside
    v-if="!mobile || (mobile && !minimized)"
    class="custom-sidebar"
    :class="{
      'custom-sidebar--minimized': minimized,
      'custom-sidebar--mobile': mobile,
      'custom-sidebar--animated': animated,
    }"
    :style="{ width: minimized ? minimizedWidth : sidebarWidth }"
  >
    <!-- Sidebar Content -->
    <nav class="sidebar-nav">
      <template v-for="(route, index) in filteredRoutes" :key="index">
        <!-- Routes with children -->
        <div v-if="route.children && route.children.length > 0" class="sidebar-group">
          <!-- Minimized: Show dropdown on hover -->
          <div
            v-if="minimized"
            :ref="(el) => setItemRef(el as HTMLElement | null, index)"
            class="sidebar-item-wrapper"
            @mouseenter="onAnchorEnter(index)"
            @mouseleave="onAnchorLeave(index)"
          >
            <div
              class="sidebar-item"
              :class="{ 'sidebar-item--active': routeHasActiveChild(route) }"
              :title="t(route.displayName)"
            >
              <span class="sidebar-item__icon material-icons">{{ route.meta.icon }}</span>
            </div>

            <!-- Teleported dropdown for minimized state -->
            <Teleport to="body">
              <Transition name="dropdown-fade">
                <div
                  v-if="dropdownStates[index]"
                  class="sidebar-dropdown"
                  :style="{
                    position: 'fixed',
                    top: (positions[index]?.top || 0) + 'px',
                    left: (positions[index]?.left || 0) + 'px',
                    zIndex: 1200,
                  }"
                  @mouseenter="onDropdownEnter(index)"
                  @mouseleave="onDropdownLeave(index)"
                >
                  <div class="sidebar-dropdown__title">
                    {{ t(route.displayName) }}
                  </div>
                  <RouterLink
                    v-for="(childRoute, index2) in route.children"
                    :key="index2"
                    :to="{ name: childRoute.name }"
                    class="sidebar-dropdown__item"
                    :class="{ 'sidebar-dropdown__item--active': isActiveChildRoute(childRoute) }"
                  >
                    {{ t(childRoute.displayName) }}
                  </RouterLink>
                </div>
              </Transition>
            </Teleport>
          </div>

          <!-- Expanded: Show collapsible menu -->
          <div v-else class="sidebar-collapse">
            <div
              class="sidebar-item sidebar-item--collapse"
              :class="{ 'sidebar-item--active': routeHasActiveChild(route) }"
              @click="toggleCollapse(index)"
            >
              <span class="sidebar-item__icon material-icons">{{ route.meta.icon }}</span>
              <span class="sidebar-item__title">{{ t(route.displayName) }}</span>
              <span class="sidebar-item__arrow material-icons">
                {{ value[index] ? 'expand_less' : 'expand_more' }}
              </span>
            </div>

            <!-- Collapse content -->
            <Transition name="collapse">
              <div v-if="value[index]" class="sidebar-collapse__content">
                <RouterLink
                  v-for="(childRoute, index2) in route.children"
                  :key="index2"
                  :to="{ name: childRoute.name }"
                  class="sidebar-item sidebar-item--child"
                  :class="{ 'sidebar-item--active': isActiveChildRoute(childRoute) }"
                >
                  <span class="sidebar-item__title">{{ t(childRoute.displayName) }}</span>
                </RouterLink>
              </div>
            </Transition>
          </div>
        </div>

        <!-- Routes without children -->
        <RouterLink
          v-else
          :to="{ name: route.name }"
          class="sidebar-item"
          :class="{ 'sidebar-item--active': routeHasActiveChild(route) }"
          :title="t(route.displayName)"
        >
          <span class="sidebar-item__icon material-icons">{{ route.meta.icon }}</span>
          <span v-if="!minimized" class="sidebar-item__title">{{ t(route.displayName) }}</span>
        </RouterLink>
      </template>
    </nav>

    <!-- AppNavbarActions at the end of sidebar -->
    <div class="sidebar-actions" :class="{ 'sidebar-actions--minimized': minimized }">
      <AppNavbarActions />
    </div>
  </aside>
</template>

<script lang="ts">
import { defineComponent, watch, ref, computed, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '../../stores/auth-store'
import AppNavbarActions from '../navbar/components/AppNavbarActions.vue'
import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  components: {
    AppNavbarActions,
  },
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
    minimized: { type: Boolean, default: false },
    animated: { type: Boolean, default: true },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const route = useRoute()
    const { t } = useI18n()
    const { roles } = storeToRefs(useAuthStore())

    const hasAccess = (r: INavigationRoute) => !r.meta?.roles || r.meta.roles.some((role) => roles.value.includes(role))
    const filteredRoutes = computed<INavigationRoute[]>(() => {
      return navigationRoutes.routes
        .map((r) => {
          const children = r.children?.filter(hasAccess)
          const visible = hasAccess(r) || (children && children.length > 0)
          if (!visible) return null as unknown as INavigationRoute
          return { ...r, ...(children ? { children } : {}) }
        })
        .filter(Boolean) as INavigationRoute[]
    })

    const value = ref<boolean[]>(new Array(filteredRoutes.value.length).fill(false))
    const dropdownStates = ref<boolean[]>(new Array(filteredRoutes.value.length).fill(false))
    const itemRefs = ref<(HTMLElement | null)[]>([])
    const positions = ref<{ top: number; left: number }[]>([])
    const hoverTimers = ref<number[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.name === section.name
      }
      return section.children.some(({ name }) => route.name === name)
    }

    const setActiveExpand = () => {
      const newValue = filteredRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route))
      if (JSON.stringify(newValue) !== JSON.stringify(value.value)) {
        value.value = newValue
      }
    }

    const toggleCollapse = (index: number) => {
      value.value[index] = !value.value[index]
    }

    const setItemRef = (el: HTMLElement | null, index: number) => {
      itemRefs.value[index] = el
    }

    const computePosition = (index: number) => {
      const el = itemRefs.value[index]
      if (!el) return
      const rect = el.getBoundingClientRect()
      positions.value[index] = {
        top: rect.top,
        left: rect.right + 8, // 8px gap to the right
      }
    }

    const clearTimer = (index: number) => {
      const timer = hoverTimers.value[index]
      if (timer) {
        window.clearTimeout(timer)
        hoverTimers.value[index] = 0 as unknown as number
      }
    }

    const showDropdown = async (index: number) => {
      clearTimer(index)
      dropdownStates.value[index] = true
      await nextTick()
      computePosition(index)
    }

    const hideDropdown = (index: number) => {
      dropdownStates.value[index] = false
    }

    // Hover guards to prevent flicker between anchor and dropdown
    const onAnchorEnter = (index: number) => {
      clearTimer(index)
      showDropdown(index)
    }

    const onAnchorLeave = (index: number) => {
      clearTimer(index)
      hoverTimers.value[index] = window.setTimeout(() => hideDropdown(index), 120)
    }

    const onDropdownEnter = (index: number) => {
      clearTimer(index)
    }

    const onDropdownLeave = (index: number) => {
      clearTimer(index)
      hideDropdown(index)
    }

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '240px'))
    const minimizedWidth = computed(() => (props.mobile ? '0' : '4.5rem'))

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    watch(
      () => props.minimized,
      (isMinimized) => {
        if (isMinimized) {
          value.value = new Array(filteredRoutes.value.length).fill(false)
        } else {
          setActiveExpand()
        }
      },
    )

    return {
      writableVisible,
      sidebarWidth,
      minimizedWidth,
      value,
      dropdownStates,
      itemRefs,
      positions,
      filteredRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      toggleCollapse,
      showDropdown,
      hideDropdown,
      setItemRef,
      onAnchorEnter,
      onAnchorLeave,
      onDropdownEnter,
      onDropdownLeave,
      t,
    }
  },
})
</script>

<style lang="scss" scoped>
.custom-sidebar {
  position: relative;
  height: 100%;
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  z-index: 1;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);

  &--animated {
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &--minimized {
    .sidebar-nav {
      align-items: center;
      padding: 0.5rem;
    }
  }

  /* overlay and stacking handled by VaLayout; no extra z-index here */
}

.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
}

.sidebar-item-wrapper {
  position: relative;
}

.sidebar-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  margin-bottom: 0.25rem;
  border-radius: 0.5rem;
  cursor: pointer;
  text-decoration: none;
  color: #4b5563;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: rgba(59, 130, 246, 0.08);
    color: #2563eb;

    .sidebar-item__icon {
      color: #2563eb;
    }
  }

  &--active {
    background-color: rgba(59, 130, 246, 0.1);
    color: #2563eb;
    font-weight: 600;

    .sidebar-item__icon {
      color: #2563eb;
    }

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 60%;
      background-color: #2563eb;
      border-radius: 0 4px 4px 0;
    }
  }

  &--collapse {
    justify-content: space-between;
  }

  &--child {
    padding-left: 3rem;
    font-size: 0.85rem;
  }

  &__icon {
    width: 20px;
    height: 20px;
    font-size: 20px;
    margin-right: 0.75rem;
    color: #6b7280;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    .custom-sidebar--minimized & {
      margin-right: 0;
    }
  }

  &__title {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__arrow {
    width: 20px;
    height: 20px;
    font-size: 20px;
    color: #9ca3af;
    transition: transform 0.3s ease;
  }
}

.sidebar-group {
  margin-bottom: 0.25rem;
}

.sidebar-collapse__content {
  overflow: hidden;
}

.sidebar-dropdown {
  position: absolute;
  left: calc(100% + 0.5rem);
  top: 0;
  min-width: 200px;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  padding: 0.5rem;
  z-index: 100;
  border: 1px solid #e5e7eb;

  &__title {
    font-weight: 600;
    font-size: 0.85rem;
    color: #374151;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 0.25rem;
  }

  &__item {
    display: block;
    padding: 0.5rem 0.75rem;
    color: #4b5563;
    text-decoration: none;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    transition: all 0.2s ease;
    margin-bottom: 0.125rem;

    &:hover {
      background-color: rgba(59, 130, 246, 0.08);
      color: #2563eb;
    }

    &--active {
      background-color: rgba(59, 130, 246, 0.1);
      color: #2563eb;
      font-weight: 600;
    }
  }
}

.sidebar-actions {
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: center;
  background: white;

  &--minimized {
    padding: 0.5rem;

    :deep(.app-navbar-actions) {
      flex-direction: column;
      gap: 0.5rem;
    }

    :deep(.app-navbar-actions__item) {
      margin: 0;
    }
  }
}

/* removed custom overlay; VaLayout manages overlay behavior */

/* Transitions */
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.3s ease;
}

.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

.collapse-enter-to,
.collapse-leave-from {
  max-height: 500px;
  opacity: 1;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.dropdown-fade-enter-to,
.dropdown-fade-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
